import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";

/**
 * Acknowledge-from-email (ADR-0171 §4.3). Public, sessionless route at `/ack`,
 * beside `/reset-password`.
 *
 * Two-step by design: mail scanners prefetch links (GETs), so the GET here is
 * side-effect-free and the acknowledge itself is the button's POST. Nothing
 * beyond the item's title is ever rendered; invalid and expired links get one
 * uniform message (no oracle).
 */

type Status =
  | { kind: "probing" }
  | { kind: "invalid" }
  | { kind: "ready"; title: string }
  | { kind: "already_acknowledged"; title: string; usedAt: string }
  | { kind: "already_closed"; title: string; status: string }
  | { kind: "done"; title: string };

function humanStatus(s: string): string {
  return s.replace(/_/g, " ");
}

export function AcknowledgeActionItem() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [status, setStatus] = useState<Status>({ kind: "probing" });
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus({ kind: "invalid" });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch<{
          valid: boolean;
          item_title?: string;
          item_status?: string;
          used_at?: string | null;
        }>(`/api/notifications/ack/status?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (!res.valid || !res.item_title) {
          setStatus({ kind: "invalid" });
        } else if (res.used_at) {
          setStatus({ kind: "already_acknowledged", title: res.item_title, usedAt: res.used_at });
        } else if (res.item_status && res.item_status !== "open") {
          setStatus({ kind: "already_closed", title: res.item_title, status: res.item_status });
        } else {
          setStatus({ kind: "ready", title: res.item_title });
        }
      } catch {
        if (!cancelled) setStatus({ kind: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function acknowledge() {
    if (status.kind !== "ready") return;
    setPosting(true);
    setError(null);
    try {
      const res = await apiFetch<{ ok: boolean; item_title: string; new_status: string }>("/api/notifications/ack", {
        method: "POST",
        body: { token }
      });
      if (res.new_status === "acknowledged") {
        setStatus({ kind: "done", title: res.item_title });
      } else {
        setStatus({ kind: "already_closed", title: res.item_title, status: res.new_status });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="centerPage">
      <div className="card pageNarrow wFull">
        {status.kind === "probing" && <div className="muted">Checking link…</div>}

        {status.kind === "invalid" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>
              Link no longer valid
            </div>
            <div className="muted">This link is no longer valid. Open Atlas to see your items.</div>
          </div>
        )}

        {status.kind === "ready" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>
              Acknowledge "{status.title}"?
            </div>
            <div className="muted" style={{ marginBottom: 16 }}>
              Acknowledging marks the item as yours and stops its reminders.
            </div>
            <button className="btn btnPrimary" onClick={() => void acknowledge()} disabled={posting}>
              Acknowledge
            </button>
            {error && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 800, color: "var(--danger-text)", marginBottom: 4 }}>Error</div>
                <div className="muted">{error}</div>
              </div>
            )}
          </div>
        )}

        {status.kind === "already_acknowledged" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>
              Already acknowledged
            </div>
            <div className="muted">
              This item was already acknowledged {new Date(status.usedAt).toLocaleString()}.
            </div>
          </div>
        )}

        {status.kind === "already_closed" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>
              Nothing to do
            </div>
            <div className="muted">
              This item is already {humanStatus(status.status)} — nothing to do.
            </div>
          </div>
        )}

        {status.kind === "done" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>
              Acknowledged
            </div>
            <div className="muted">
              "{status.title}" is now marked acknowledged — reminders for it stop.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
